/**
 * What the BIOTECHNICIAN's five boards share: the ground each era grows in, the leaf every plant
 * is drawn with, the clay pot, and the how-to played through a board's own rules.
 *
 * A game of the thirty is played at two different stops, so every board here takes its soil,
 * stems and leaves from `GROUND[era]`: dark garden soil on Earth, a lit tray in the city, scorched
 * earth in the war, sand underground, rich loam back on the surface.
 *
 * Each board keeps its round in one plain object made by its rules (`vineRound`, `beesRound`, …):
 * the live board drives it from the guest's finger and the kit's game clock, and the how-to drives
 * the very same object from the ghost finger (`playDemo`), so the demo is the game itself.
 */
import type { DemoKey, FingerKey, Fx, Pt } from '../kit.js';
import { HIT_MS, MISS_MS, fingerAt, q } from '../kit.js';
import type { Era } from '../../art.js';

export interface Ground {
  /** The soil, top to bottom. */
  soilTop: string;
  soilBottom: string;
  /** Specks, pebbles and grains in the soil. */
  fleck: string;
  /** Furrows, tray lines, cracks, ripples: the era's texture. */
  line: string;
  /** A living stem, its shadow side, a leaf and a leaf's lit edge. */
  stem: string;
  stemDark: string;
  leaf: string;
  leafHi: string;
  /** The warm light of a seed or a lit thing. */
  glow: string;
  /** Stones and rocks. */
  rock: string;
  rockHi: string;
}

export const GROUND: Record<Era, Ground> = {
  stars: {
    soilTop: '#2b1e15',
    soilBottom: '#120b07',
    fleck: '#5a4231',
    line: '#6fb7ff',
    stem: '#3f9a45',
    stemDark: '#1f4f25',
    leaf: '#55b957',
    leafHi: '#b9f28c',
    glow: '#ffd98a',
    rock: '#5b6472',
    rockHi: '#aab6c6',
  },
  neon: {
    soilTop: '#1a0f2c',
    soilBottom: '#0a0614',
    fleck: '#3b2a5c',
    line: '#4fe0ff',
    stem: '#2fc98a',
    stemDark: '#105a44',
    leaf: '#3ee6a4',
    leafHi: '#c2ffe8',
    glow: '#ff9ff0',
    rock: '#4a4466',
    rockHi: '#a79fd6',
  },
  debris: {
    soilTop: '#2a1411',
    soilBottom: '#110606',
    fleck: '#4d2a22',
    line: '#ff8a3c',
    stem: '#5c9a3c',
    stemDark: '#2c4a1c',
    leaf: '#7cbf4f',
    leafHi: '#d6f29a',
    glow: '#ffcf6b',
    rock: '#5e4a46',
    rockHi: '#b39a90',
  },
  dust: {
    soilTop: '#4a3218',
    soilBottom: '#1d1208',
    fleck: '#7a5a34',
    line: '#ffd8a0',
    stem: '#d9b27c',
    stemDark: '#7a5530',
    leaf: '#e8c898',
    leafHi: '#fff0d6',
    glow: '#fff1c2',
    rock: '#6d5236',
    rockHi: '#c9a878',
  },
  canopy: {
    soilTop: '#26301a',
    soilBottom: '#0e1509',
    fleck: '#46592d',
    line: '#d6f5a8',
    stem: '#4f9a3c',
    stemDark: '#244a1a',
    leaf: '#78c65e',
    leafHi: '#dcf9b0',
    glow: '#ffe08a',
    rock: '#56604a',
    rockHi: '#b4c09c',
  },
};

/** A clay pot's colours, the same on the potting bench and on the shelf. */
export const CLAY = {
  shade: '#8e3f22',
  lit: '#d9774a',
  body: '#b85a33',
  deep: '#6e2e18',
  rim: '#d27048',
  edge: '#4e200e',
  shine: '#ffd0b0',
  earth: '#2a1a10',
} as const;

/** A leaf pointing along +x from the origin, 10 units long. */
export const LEAF_D = 'M0 0 C 2.4 -3.6, 7 -4, 10 0 C 7 3.4, 2.4 3, 0 0 Z';

/** Fixed specks for a patch of soil, 0–1 across and down: the same ground every render. */
export const SPECKS: readonly { x: number; y: number; r: number }[] = [
  { x: 0.06, y: 0.12, r: 1.1 },
  { x: 0.17, y: 0.55, r: 0.8 },
  { x: 0.24, y: 0.86, r: 1.3 },
  { x: 0.31, y: 0.27, r: 0.7 },
  { x: 0.39, y: 0.68, r: 1 },
  { x: 0.46, y: 0.09, r: 0.9 },
  { x: 0.53, y: 0.44, r: 1.2 },
  { x: 0.61, y: 0.81, r: 0.8 },
  { x: 0.68, y: 0.2, r: 1.1 },
  { x: 0.74, y: 0.58, r: 0.7 },
  { x: 0.82, y: 0.35, r: 1.3 },
  { x: 0.88, y: 0.9, r: 0.9 },
  { x: 0.94, y: 0.15, r: 0.8 },
  { x: 0.11, y: 0.38, r: 0.9 },
  { x: 0.57, y: 0.96, r: 1 },
  { x: 0.97, y: 0.66, r: 1.1 },
];

export const clamp = (v: number, lo = 0, hi = 1) => Math.min(hi, Math.max(lo, v));
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;

/**
 * A slow swing between 0 and 1 drawn from the game clock `t` (ms), once every `periodMs`: what a
 * sway, a bob or a soft pulse is drawn from (never a CSS animation, so every screen draws the same).
 */
export const wave = (t: number, periodMs: number, phase = 0) => 0.5 + 0.5 * Math.sin((t / periodMs) * Math.PI * 2 + phase);

/** Mixes two #rrggbb colours. */
export function mix(a: string, b: string, t: number): string {
  const pa = parseInt(a.slice(1), 16);
  const pb = parseInt(b.slice(1), 16);
  const ch = (p: number, s: number) => (p >> s) & 255;
  const out = [16, 8, 0].map((s) => Math.round(lerp(ch(pa, s), ch(pb, s), clamp(t))));
  return `#${out.map((v) => v.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * What a board's rules tell the round: a move that counts, a mistake, how far through, the win.
 * The kit's `useRound()` is one; a how-to uses `demoRound()`.
 */
export interface Feedback {
  hit: () => void;
  miss: () => void;
  progress: (fraction: number) => void;
  win: () => void;
}

/** Feedback handed on to whatever round the board holds now (the kit's round is new every render). */
export function feedbackTo(round: { current: Feedback | null }): Feedback {
  return {
    hit: () => round.current?.hit(),
    miss: () => round.current?.miss(),
    progress: (f) => round.current?.progress(f),
    win: () => round.current?.win(),
  };
}

/**
 * A how-to's stand-in for the kit's round: the same feedback on the demo's own clock (`now`, set
 * by `playDemo`), and the glow and the shake as the kit would draw them.
 */
export function demoRound() {
  const r = {
    now: 0,
    hitAt: -1e9,
    missAt: -1e9,
    won: false,
    hit() {
      r.hitAt = r.now;
    },
    miss() {
      if (!r.won) r.missAt = r.now;
    },
    progress(_fraction: number) {},
    win() {
      if (r.won) return;
      r.won = true;
      r.hitAt = r.now;
    },
    /** The glow and the shake now. Always a pair, so a demo glides them out. */
    fx(): Fx {
      return [q(Math.max(0, 1 - (r.now - r.hitAt) / HIT_MS), 2), q(Math.max(0, 1 - (r.now - r.missAt) / MISS_MS), 2)];
    },
  };
  return r;
}

/** A finger on a board, as a round of the bio boards takes it: in board units, on the game clock. */
export interface FingerInput {
  down: (p: Pt, now: number) => void;
  move: (p: Pt, now: number) => void;
  up: (now: number) => void;
}

/** How often a how-to is worked out, and how often it is keyed while nothing jumps. */
const DEMO_TICK = 10;
const DEMO_EVERY = 50;

/**
 * A how-to played through the game's own rules: the ghost finger's script presses, drags and lets
 * go on `input` exactly as a guest's finger would, `step` runs the game's clock, and the board is
 * keyed from `view` every 50 ms — and again at the very moment `parts(view)` changes: whatever
 * must jump rather than glide (a count, a state, a list that grows, a cell the vine moves into).
 */
export function playDemo<V extends object>(
  ms: number,
  finger: readonly FingerKey[],
  round: { now: number },
  game: { input: FingerInput; step: (dt: number, now: number) => void; view: (now: number) => V },
  parts: (view: V) => unknown,
): DemoKey<V>[] {
  const script = { ms, keys: [], finger, steps: [] };
  const keys: DemoKey<V>[] = [];
  let pressed = false;
  let last: Pt | null = null;
  let before: V | null = null;
  let mark = '';
  for (let t = 0; t <= ms; t += DEMO_TICK) {
    round.now = t;
    const f = fingerAt(script, t);
    const p = { x: q(f.x, 1), y: q(f.y, 1) };
    if (f.down && !pressed) game.input.down(p, t);
    else if (f.down && last && (last.x !== p.x || last.y !== p.y)) game.input.move(p, t);
    else if (!f.down && pressed) game.input.up(t);
    pressed = f.down;
    last = p;
    if (t > 0) game.step(DEMO_TICK / 1000, t);
    const view = game.view(t);
    const now = JSON.stringify(parts(view));
    if (before === null) keys.push({ at: t, view });
    else if (now !== mark) {
      // Glide up to the frame before, then jump.
      if (keys[keys.length - 1]!.at < t - DEMO_TICK) keys.push({ at: t - DEMO_TICK, view: before });
      keys.push({ at: t, view, ease: 'step' });
    } else if (t % DEMO_EVERY === 0) keys.push({ at: t, view });
    mark = now;
    before = view;
  }
  return keys;
}
